import { Box, FormGroup, GlobalStyles, InputLabel } from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import useCanEditField from 'shared/hooks/useCanEditField';
import { Updater } from 'use-immer';
import CustomIcon from 'components/CustomIcon';
import { toTitleCase, resolveIsRequired } from 'shared/utilities';
import useCanViewField from 'shared/hooks/useCanViewField';
import ToggleableFieldValue from './ToggleableFieldValue';
import ToggleableFieldField from './ToggleableFieldField';
import FieldRoleVisibility from './FieldRoleVisibility';

// Stable class name used by children (ToggleableFieldField, ToggleableFieldValue)
// to apply the label typography styles without needing a JSS class map.
const LABEL_CLASS = 'ToggleableField-label';

const labelStyles = {
  [`.${LABEL_CLASS}`]: {
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 'normal',
    position: 'relative',
    marginBottom: '2px',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
  },
};

// Exposed so children can import and use directly
export const parentClasses = { label: LABEL_CLASS };

interface Props {
  field: ToggleableField;
  hasAttemptedSave: boolean;
  isEditing: boolean;
  labelGetter: (fieldName: string, route?: string) => string;
  renderAsList?: boolean;
  edits: EditableCompany | EditablePerson;
  setEdits: Updater<EditableCompany | EditablePerson>;
  type?: ProfileType;
  useCappedLabels?: boolean;
  useIcon?: boolean;
  value: any;
  route?: string
  fieldData?: any
}

const ToggleableField: React.FC<Props> = ({
  field, hasAttemptedSave, isEditing, edits, setEdits, labelGetter,
  renderAsList, type, useCappedLabels, useIcon, value, route, fieldData,
}: Props) => {
  const canEditField = useCanEditField();
  const canViewField = useCanViewField();
  const TYPES_WITH_RADIOS_OR_CHECKBOXES = [
    'boolean',
    'boolean-explain',
    'booolean-two-part',
    'checkbox',
    'criminal-history',
    'enum-radio',
    'shipping',
    'radio',
  ];

  const TYPES_THAT_SPAN_COLUMNS = [
    'company-logo', 'form-instructions', 'subhead',
  ];

  const canEdit = canEditField(field, type);
  const canView = canViewField(field, type);

  if (!canView) {
    return null;
  }

  const labelKey = field.subFieldName && field.subFieldName !== 'data'
    ? `${field.subFieldName}/${field.fieldName}`
    : field.fieldName || '';
  const fieldLabel = labelGetter(labelKey, route);

  const inner = (
    <>
      {!field.noLabel && (
        <InputLabel
          htmlFor={field.fieldName || ''}
          className={LABEL_CLASS}
          required={Boolean(resolveIsRequired(field, edits) && canEdit)}
          sx={{
            fontSize: useCappedLabels ? '0.85rem' : '1rem',
            fontWeight: 700,
            lineHeight: 'normal',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          }}
        >
          <Box style={{ position: 'relative', display: 'inline-block', paddingBottom: '5px' }}>
            {field.labelOverride || useCappedLabels ? toTitleCase(fieldLabel) : fieldLabel}
            <FieldRoleVisibility
              roles={field?.canView || []}
            />
          </Box>
        </InputLabel>
      )}

      {isEditing && canEdit ? (
        <ToggleableFieldField
          edits={edits}
          field={field}
          hasAttemptedSave={hasAttemptedSave}
          parentClasses={parentClasses}
          setEdits={setEdits}
          value={value}
          fieldData={fieldData}
        />
      ) : (
        <ToggleableFieldValue
          field={field}
          parentClasses={parentClasses}
          renderAsList={renderAsList}
          value={value}
        />
      )}
    </>
  );

  const content = TYPES_WITH_RADIOS_OR_CHECKBOXES.includes(field.type)
    ? <FormGroup>{inner}</FormGroup>
    : <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', marginBottom: '1rem' }}>{inner}</Box>;

  return (
    <>
      <GlobalStyles styles={labelStyles} />
      <IconRow
        noIcon={!useIcon}
        size="small"
        className={clsx(
          TYPES_THAT_SPAN_COLUMNS.includes(field.type) && 'wide-column',
          field.isWide && 'wide-column',
          field.clearRow && 'clear-row',
          field.indented && 'indented-field',
        )}
        sx={{
          ...(useCappedLabels && { [`& .${LABEL_CLASS}`]: { fontSize: '0.85rem' } }),
          '&.wide-column': { gridColumn: '1 / -1' },
          '&.clear-row': { gridColumn: 1 },
          '&.indented-field': {
            pl: 2,
            borderLeft: (t) => `2px solid ${t.palette.grey1}`,
          },
        }}
      >
        {useIcon && (
          <IconRowCell type="icon">
            {field.icon && (
              <CustomIcon
                icon={field.icon}
                fontSize="small"
              />
            )}
          </IconRowCell>
        )}
        <IconRowCell type="text">{content}</IconRowCell>
      </IconRow>
    </>
  );
};

export default ToggleableField;
